import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ConnectorState } from './index';

/**
 * 3-arm, trigger-gated Statsig experiment that controls how admin-connected 3P
 * "full connectors" are seeded for Rovo Chat.
 *
 * Outer gate: `ai_3p_cds_auth_resolution_mode_flock`. The experiment is only
 * evaluated for tenants where this flock rollout gate is ON. Everywhere the gate
 * is OFF the baseline (`control`, no seeding, no exposure) is returned, so the
 * experiment ships inside the controlled flock rollout.
 *
 * Bucketing unit: tenantID (configured on the experiment in Switcheroo/Statsig —
 * not something the client passes to `expVal`). Every user in a triggered tenant
 * is bucketed into the same arm, so the chat experience is consistent across the
 * tenant and matches the OAuth-agnostic, tenant-level trigger below.
 *
 * Parameters (configured ON the experiment — never hardcoded in the client):
 *  - `cohort`: 'control' | 'treatment-1' | 'treatment-2'
 *     - `control`      — baseline. A connector is only usable when the user has
 *                        personally OAuthed it (`isUserOAuthed === true`).
 *     - `treatment-1`  — identity-mapped only. An admin-imported
 *                        (`ADMIN_IMPORTED_USER_LINK`) connector is surfaced as
 *                        connected (toggle ON) so it is queryable/searchable.
 *     - `treatment-2`  — all users. Every eligible full connector is seeded into
 *                        the chat request for all users in the tenant, regardless
 *                        of OAuth or identity mapping, so downstream tool
 *                        invocation / auth-card prompts can run.
 *  - `connector_allowlist`: `{ connectors: string[] }` — the eligible full-connector
 *     `resourceType`s the experiment targets (e.g. `github-connector`,
 *     `google-drive-connector`). This list lives on the experiment config so the
 *     targeted connector set can change without a code change and stays consistent
 *     across every arm.
 *
 * Keep the full identifier ≤ 50 chars.
 */
export const ADMIN_CONNECTED_SEEDING_EXPERIMENT = 'rovo-chat-auth-card-admin-connected-seeding-exp';

/** Outer rollout gate: the experiment only runs where this flock gate is enabled. */
export const ADMIN_CONNECTED_SEEDING_GATE = 'ai_3p_cds_auth_resolution_mode_flock';

export type AdminConnectedSeedingCohort = 'control' | 'treatment-1' | 'treatment-2';

type ConnectorAllowlist = { connectors: string[] };

const EMPTY_CONNECTORS: readonly string[] = [];

export type AdminConnectedSeedingConfig = {
	cohort: AdminConnectedSeedingCohort;
	/** Eligible full-connector `resourceType`s, sourced from the experiment (never hardcoded). */
	eligibleConnectors: readonly string[];
};

/** A full connector (its `resourceType` ends with `-connector`), regardless of OAuth state. */
export const isFullConnector = ({ resourceType }: ConnectorState): boolean =>
	typeof resourceType === 'string' && resourceType.endsWith('-connector');

/**
 * Structural trigger pre-condition: the tenant has at least one full connector
 * present in the knowledge sources (any `*-connector`), regardless of whether the
 * viewing user has OAuthed it. Only then is the experiment read (and exposure
 * logged), so users whose tenant could never benefit do not dilute the metrics.
 * Keeping the trigger OAuth-agnostic makes cohort targeting consistent across all
 * arms and aligns with the experiment's tenantID bucketing. The specific eligible
 * connectors come from the experiment config, not this check.
 */
export const hasSeedingCandidate = (sources: ConnectorState[]): boolean =>
	sources.some(isFullConnector);

/**
 * Whether a source is one of the experiment's eligible full connectors, per the
 * experiment-provided allowlist (never a hardcoded brand list).
 */
export const isEligibleConnector = (
	source: ConnectorState,
	eligibleConnectors: readonly string[],
): boolean =>
	typeof source.resourceType === 'string' && eligibleConnectors.includes(source.resourceType);

/**
 * Resolves the cohort and eligible-connector list for the current user while
 * honouring both gates:
 *  1. Outer rollout gate (`ADMIN_CONNECTED_SEEDING_GATE`) — when OFF, the
 *     experiment is never read and baseline `control` is returned.
 *  2. Trigger discipline — even when the gate is ON, the experiment is only
 *     evaluated (and exposure only logged) when the tenant has a full connector
 *     present.
 * In either skipped case baseline `control` with an empty list is returned
 * WITHOUT logging exposure.
 */
export const resolveAdminConnectedSeedingConfig = (
	sources: ConnectorState[],
): AdminConnectedSeedingConfig => {
	if (!fg(ADMIN_CONNECTED_SEEDING_GATE)) {
		return { cohort: 'control', eligibleConnectors: EMPTY_CONNECTORS };
	}
	if (!hasSeedingCandidate(sources)) {
		return { cohort: 'control', eligibleConnectors: EMPTY_CONNECTORS };
	}
	// `expVal` widens a string-literal union back to `string`, so narrow the result
	// to a known cohort and fall back to `control` for any unexpected value.
	const rawCohort = expVal(ADMIN_CONNECTED_SEEDING_EXPERIMENT, 'cohort', 'control');
	const cohort: AdminConnectedSeedingCohort =
		rawCohort === 'treatment-1' || rawCohort === 'treatment-2' ? rawCohort : 'control';
	const eligibleConnectors =
		expVal<ConnectorAllowlist>(ADMIN_CONNECTED_SEEDING_EXPERIMENT, 'connector_allowlist', {
			connectors: [],
		}).connectors ?? EMPTY_CONNECTORS;
	return { cohort, eligibleConnectors };
};
